import {floatPriceFromFormatted} from '@optimaxdev/utils';

import {createSelector} from '@reduxjs/toolkit';

import {STEPS} from 'desktop/pages/checkout/constants/checkout';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {ApplicationStoreType} from 'desktop/store';
import {AmazonType} from 'desktop/types/amazonV2Type';
import {PayerType} from 'desktop/types/payerInfoType';

import {PaymentMethodsType} from 'types/PaymentMethodsType';

import {MODULE_KEY_CHECKOUT} from '../constants';
import {getCurrentStep} from '../currentStep/currentStep';
import {getOrderReviewTotals} from '../orderReviewTotals/orderReviewTotals';
/**
 * Get payments methods
 *
 * @param {ApplicationStoreType} store - current store
 * @return {PaymentMethodsType} payment methods - payment methods for customer
 */
export const getPaymentMethods = (store: ApplicationStoreType): PaymentMethodsType =>
    store[MODULE_KEY_CHECKOUT].paymentStep.paymentMethods;

/**
 * Get payment method loading
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - payment method is loading
 */
export const getPaymentMethodsLoading = createSelector(
    getPaymentMethods,
    (methods: PaymentMethodsType) => methods.status === 'PENDING',
);

/**
 * Get selected payment method
 *
 * @param {ApplicationStoreType} store - current store
 * @return {PAYMENTS} - payment data
 */
export const getNameSelectedMethod = (store: ApplicationStoreType): PAYMENTS =>
    store[MODULE_KEY_CHECKOUT].paymentStep.selectedMethod;

/**
 * Get one step payment is editing or not
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - is editing or not
 */
export const getIsEditingOneStepPayment = (store: ApplicationStoreType): boolean =>
    store[MODULE_KEY_CHECKOUT].paymentStep.oneStep.isEditingOneStepMethod;

/**
 * Get payment method of list methods
 *
 * @param {ApplicationStoreType} store - current store
 * @return {PaymentType} - payment method
 */
export const getSelectedMethod = createSelector(
    getPaymentMethods,
    getNameSelectedMethod,
    (methods: PaymentMethodsType, selected: PAYMENTS) => methods.list[selected],
);

/**
 * Get payment payment method by key
 *
 * @param {ApplicationStoreType} store - current store
 * @param {PAYMENTS} type - current type
 * @return {PaymentType} - payment method
 */
export const getPaymentMethodByKey = createSelector(
    [getPaymentMethods, (store: ApplicationStoreType, type: PAYMENTS) => type],
    (methods: PaymentMethodsType, type: PAYMENTS) =>
        methods.list[type] || {
            code: '',
            image: '',
            installments: [],
            order: 0,
            selected: false,
            title: '',
        },
);

/**
 * Get payment payment method selected by key
 *
 * @param {ApplicationStoreType} store - current store
 * @param {PAYMENTS} type - current type
 * @return {boolean} - payment method is selected
 */
export const getIsSelectedByKey = createSelector(
    [
        getPaymentMethods,
        getNameSelectedMethod,
        (store: ApplicationStoreType, type: PAYMENTS) => type,
    ],
    (methods: PaymentMethodsType, selectedMethod: string, type: PAYMENTS) =>
        methods.list[type]?.code === selectedMethod,
);

/**
 * Get payer info
 *
 * @param {ApplicationStoreType} store - current store
 * @return {PayerType} - payer info
 */
export const getPayerInfo = (store: ApplicationStoreType): PayerType =>
    store[MODULE_KEY_CHECKOUT].paymentStep.payerInfo;

/**
 * Get amazon data
 *
 * @param {ApplicationStoreType} store - current store
 * @return {AmazonType} - amazon data
 */
export const getAmazonV2 = (store: ApplicationStoreType): AmazonType =>
    store[MODULE_KEY_CHECKOUT].paymentStep.amazonV2;

/**
 * Get is amazon payment confirmed to run create order
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - is amazon payment confirmed
 */
export const getAmazonV2Confirmed = createSelector(
    getAmazonV2,
    amazonV2 => amazonV2.paymentConfirmed,
);

/**
 * Get message error
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} - message error
 */
export const getMessageError = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].paymentStep.savePaymentData.message;

/**
 * Get payment method of list methods
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - payment method
 */
export const getStatusOfLoadingSaveOrder = (store: ApplicationStoreType): boolean =>
    store[MODULE_KEY_CHECKOUT].paymentStep.savePaymentData.isLoading;

/**
 * Get payment step is completed
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - payment step is completed
 */
export const getPaymentStepIsCompleted = (store: ApplicationStoreType) =>
    store[MODULE_KEY_CHECKOUT].status[STEPS.PAYMENT_STEP].completed;

/**
 * Get save payment data is loading
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - save payment data is loading
 */
export const getSavePaymentDataIsLoading = (store: ApplicationStoreType): boolean =>
    store[MODULE_KEY_CHECKOUT].paymentStep.savePaymentData.status === 'PENDING';

/**
 * Format totals for GooglePay
 */
export const getTotalsForGooglePay = createSelector(getOrderReviewTotals, checkout => ({
    total: floatPriceFromFormatted(checkout.grandTotal).toString(),
}));

/**
 * Get is payment method loading
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - payment method is loading
 */
export const getIsPaymentMethodsLoading = createSelector(
    getCurrentStep,
    getPaymentMethodsLoading,
    getPaymentStepIsCompleted,
    (currentStep, isLoading, isCompleted) =>
        currentStep === STEPS.PAYMENT_STEP && isLoading && !isCompleted,
);

/**
 * Get payment method fulfilled
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {boolean} - is payment method fulfilled
 */
export const getIsPaymentMethodFulfilled = (store: ApplicationStoreType) =>
    getPaymentMethods(store).status === 'FULFILLED';
